import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { autoDisplayValue, formatSplitKey } from "../../../commons/FormatValue";
import { filteredItemInArray } from "../../../utils/ManageArray";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import { formatInputDate, monthsDifference } from "../../../utils/Time";
import { initializeSetOptionDisabled } from "../../../utils/InitializeSetDefault";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../../FormComponent";
import TableForm from "../../TableForm";
import ConfirmModal from "../../ConfirmModal";
import CardDisplayComponent from "../../CardDisplayComponent";
import CustContractItemsService from "../../../services/custcontractitem.service";
import DefineAgeEquipsService from "../../../services/defineageequips.service";
import ParentBomsService from "../../../services/parentboms.service";
import ChildBomsService from "../../../services/childboms.service";

const DetermineAgeForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [contractItem, setContractItem] = useState([]);
  const [defineAge, setDefineAge] = useState([]);
  const [parentBoms, setParentBoms] = useState([]);
  const [childBoms, setChildBoms] = useState([]);
  const defaultData = {
    Contract_Id: "",
    Contract_No: "",
    Item_Id: "",
    Child_Bom_Id: "",
    Item_Age: "",
    Age_Start_Date: "",
    Age_End_Date: "",
    Remark: "",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [defaultData] });
  const rowData = data?.Array?.[0];
  const inputItem = [
    {
      name: "Contract_No",
      label: "เลขที่สัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "select",
      filterBy: ["Contract_No"],
      options: initializeSetOptionDisabled(
        { key: "Contract_No", dataset: contractItem },
        {
          key: "Contract_No",
          data: filteredItemInArray(
            defineAge,
            "Contract_No",
            rowData?.Contract_No
          )
        }
      ),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest:
            CustContractItemsService.getAllCustContractItems(query),
          callback: ({ items }) => setContractItem(items)
        })
    },
    {
      name: "Contract_Type",
      label: "สถานะสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      findValue: { data: contractItem, value: "Contract_No" }
    },
    {
      name: "CustContract.Contract_Start_Date",
      label: "วันที่เริ่มต้นสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      findValue: { data: contractItem, value: "Contract_No" }
    },
    {
      name: "CustContract.Contract_End_Date",
      label: "วันที่สิ้นสุดสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      findValue: { data: contractItem, value: "Contract_No" }
    },
    {
      name: "CustContract.Project_No",
      label: "เลขที่โครงการ",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      findValue: { data: contractItem, value: "Contract_No" }
    },
    {
      name: "CustContract.Customer.Cus_Code",
      label: "รหัสลูกค้า/สาขา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      findValue: { data: contractItem, value: "Contract_No" }
    },
    {
      name: "CustContract.Customer.Cus_Name",
      label: "ซื่อลูกค้า/สาขา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      findValue: { data: contractItem, value: "Contract_No" },
      size: 6
    },
    {
      name: "Item_Id",
      label: "รหัสสินค้า",
      type: "select",
      noneAsync: true,
      options: initializeSetOptionDisabled(
        {
          key: "Item_Id",
          label: "Item.Item_Code",
          dataset: filteredItemInArray(
            contractItem,
            "Contract_No",
            rowData?.Contract_No
          )
        },
        {
          key: "Item_Id",
          data: filteredItemInArray(
            defineAge,
            "Contract_No",
            rowData?.Contract_No
          )
        }
      )
    },
    {
      name: "Item_Id",
      label: "ชื่อสินค้า",
      type: "select",
      size: 9,
      noneAsync: true,
      options: initializeSetOptionDisabled(
        {
          key: "Item_Id",
          label: "Item.Item_Name_TH",
          dataset: filteredItemInArray(
            contractItem,
            "Contract_No",
            rowData?.Contract_No
          )
        },
        {
          key: "Item_Id",
          data: filteredItemInArray(
            defineAge,
            "Contract_No",
            rowData?.Contract_No
          )
        }
      )
    }
  ];
  const inputField = [
    {
      name: "Item.Item_Code",
      label: "รหัสอุปกรณ์/อะไหล่",
      type: "text",
      readOnly: true,
      findValue: { data: childBoms, key: "Id", value: "Child_Bom_Id" }
    },
    {
      name: "Item.Item_Name_TH",
      label: "รายละเอียดอุปกรณ์/อะไหล่",
      type: "text",
      size: 2,
      readOnly: true,
      findValue: { data: childBoms, key: "Id", value: "Child_Bom_Id" }
    },
    {
      name: "Item.Item_Model",
      label: "Model Number",
      type: "text",
      readOnly: true,
      findValue: { data: childBoms, key: "Id", value: "Child_Bom_Id" }
    },
    {
      name: "Item_Child_Qty",
      label: "จำนวน",
      type: "text",
      readOnly: true,
      findValue: { data: childBoms, key: "Id", value: "Child_Bom_Id" }
    },
    {
      name: "Item.Unit",
      label: "หน่วย",
      type: "text",
      readOnly: true,
      findValue: { data: childBoms, key: "Id", value: "Child_Bom_Id" }
    },
    {
      name: "Item_Age",
      label: "อายุอุปกรณ์/อะไหล่ (เดือน)",
      type: "text"
    },
    {
      name: "Age_Start_Date",
      label: "วันที่เริ่มต้น",
      type: "date",
      format: (row) => formatInputDate(row?.Age_Start_Date, "Local").date
    },
    {
      name: "Age_End_Date",
      label: "วันที่หมดอายุ",
      type: "date",
      format: (row) => formatInputDate(row?.Age_End_Date, "Local").date,
      readOnly: true
    },
    { name: "Remark", label: "หมายเหตุ", type: "text", noneRequired: true }
  ];

  const handleSearch = ({ name, label, change }) => {
    let updatedData = [];
    if (change) {
      if (name === "Contract_No") {
        updatedData = [{ ...rowData, [name]: change?.value }];
      } else {
        const findedContract = contractItem.find(
          (ct) =>
            ct?.Contract_No === rowData?.Contract_No &&
            `${ct?.Item_Id}` === change?.value
        );
        const Contract_Id = findedContract?.Id;
        const Item = findedContract?.Item;
        updatedData = childBoms
          .filter(({ Parent_Bom_Id, Bom_Pm }) =>
            parentBoms.some(
              ({ Id, Item_Parent_Id }) =>
                Bom_Pm &&
                change?.value === `${Item_Parent_Id}` &&
                Parent_Bom_Id === Id
            )
          )
          .map(({ Id }) => ({
            ...rowData,
            Contract_Id,
            Item,
            Item_Id: Number(change?.value),
            Child_Bom_Id: Id
          }));
        if (updatedData?.length === 0) {
          showAlert({
            error: "ข้อผิดพลาด",
            message: `กรุณาสร้าง Bom ของ${label} ${change?.label}`,
            type: "danger"
          });
          updatedData = data.Array;
        }
      }
    }
    setData({ Array: updatedData });
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Array[0]);
    }
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: DefineAgeEquipsService.getAllDefineAgeEquips(),
      callback: (res) => setDefineAge(res)
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ParentBomsService.getAllParentBoms(),
      callback: (res) => setParentBoms(res)
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ChildBomsService.getAllChildBoms(),
      callback: (res) => setChildBoms(res)
    });

    if (editData) {
      setData({
        Array: [
          {
            Contract_No: editData.Contract_No,
            Item_Id: editData.Item_Id,
            Child_Bom_Id: editData.Child_Bom_Id,
            Item_Age: editData.Item_Age,
            Age_Start_Date: editData.Age_Start_Date,
            Age_End_Date: editData.Age_End_Date,
            Remark: editData.Remark,
            Com_Code: editData.Com_Code
          }
        ]
      });
    }
  }, [editData]);

  return (
    <>
      <CardDisplayComponent
        header={{
          title: {
            className: "text-center",
            text: `${!editData ? "เพิ่ม" : "แก้ไข"}ข้อมูลอายุอุปกรณ์/อะไหล่`
          }
        }}
        body={{
          as: Row,
          element: inputItem.map(
            (
              {
                name,
                label,
                type,
                classNames,
                size,
                options,
                findValue,
                noneAsync,
                filterBy,
                queryService
              },
              idx
            ) => {
              const value = autoDisplayValue({
                name,
                type,
                findValue: {
                  ...findValue,
                  key: findValue?.value,
                  value: rowData?.[findValue?.value]
                }
              });

              return (
                <Col
                  key={`${name}-${idx}`}
                  sm={size || 3}
                  className={classNames?.col}
                >
                  {type === "select" ? (
                    <SearchSelectComponent
                      labelField={label}
                      placeholder="โปรดเลือก"
                      floatingClass={classNames?.label}
                      noneAsync={noneAsync}
                      filterBy={filterBy}
                      options={options}
                      queryService={queryService}
                      onChange={(change) =>
                        handleSearch({ name, label, change })
                      }
                      value={formatSplitKey(name, ".", rowData)}
                    />
                  ) : (
                    <FloatingControlComponent
                      disabled
                      type={type}
                      name={name}
                      label={label}
                      className={classNames}
                      value={value}
                    />
                  )}
                </Col>
              );
            }
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดอุปกรณ์/อะไหล่" } }}
          body={{
            element: (
              <TableForm
                editForm
                data={data.Array}
                columns={inputField}
                defaultData={defaultData}
                onChangeCallback={(newData) =>
                  setData({
                    Array: newData.map(
                      ({ Item_Age, Age_Start_Date, Age_End_Date, ...item }) => {
                        const ageMonth =
                          Number(Item_Age) > 0 && Age_Start_Date
                            ? monthsDifference(
                                new Date(Age_Start_Date),
                                Item_Age
                              )
                            : "";

                        return {
                          ...item,
                          Item_Age: Number(Item_Age),
                          Age_Start_Date,
                          Age_End_Date: ageMonth
                        };
                      }
                    )
                  })
                }
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={data}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-success"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/bind", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

DetermineAgeForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default DetermineAgeForm;
